import type { BreadcrumbList, ListItem } from 'schema-dts';
import { siteUrl, siteName } from '$lib/config';

import getCategoriesTree from '$lib/getCategoriesTree';
import type { ButterflyCategory, ButterflyRelation, ButterflyData } from '$lib/types';

function buildCategories(data: App.PageData, position: number): ListItem[] {
  let tree: ButterflyCategory[] = [];

  if (
    ['post', 'calculator', 'tag'].indexOf(data.scope.dataLayer['page.type']) >= 0 &&
    data.scope.data?.data?.relationships?.category?.data
  ) {
    tree = getCategoriesTree(
      data.scope.data.data.relationships.category.data.id,
      data.categories.data as ButterflyCategory[],
    );
  } else if (
    data.scope.dataLayer['page.type'] === 'category' &&
    data.scope.data?.relationships.parentCategory.data
  ) {
    tree = getCategoriesTree(
      data.scope.data.relationships.parentCategory.data.id,
      data.categories.data as ButterflyCategory[],
    );
  }

  return tree.map((c) => ({
    '@type': 'ListItem',
    position: position++,
    name: c.attributes.name,
    item: `${siteUrl}${c.attributes.path}`,
  }));
}

export default function getBreadcrumbList(data: App.PageData): BreadcrumbList {
  const itemListElement: ListItem[] = [];
  let position = 1;

  if (data.scope.dataLayer['page.type'] !== 'home') {
    itemListElement.push({
      '@type': 'ListItem',
      position: position++,
      name: siteName,
      item: `${siteUrl}/`,
    });
  }

  if (
    data.scope.dataLayer['page.type'] &&
    ['post', 'calculator', 'tag', 'category'].indexOf(data.scope.dataLayer['page.type']) >= 0
  ) {
    const categories = buildCategories(data, position);
    itemListElement.push(...categories);
    position = itemListElement.length
      ? (itemListElement[itemListElement.length - 1].position as number)
      : position;

    if (
      data.scope.dataLayer['page.type'] === 'post' &&
      data.scope.data.data.relationships.terms.data
    ) {
      const tag = data.scope.data.data.relationships.terms.data.find(
        (t: ButterflyRelation) => t.type === 'term<tags>',
      );
      if (tag) {
        const el = data.scope.data.included.find(
          (i: ButterflyData) => i.type === tag.type && i.id === tag.id,
        );
        if (el) {
          itemListElement.push({
            '@type': 'ListItem',
            position: position++,
            name: el.attributes.name,
            item: `${siteUrl}/tags/${el.id}`,
          });
        }
      }
    }
  } else if (
    data.scope.dataLayer['page.type'] === 'search' &&
    data.scope.dataLayer['page.query']?.length
  ) {
    itemListElement.push({
      '@type': 'ListItem',
      position: position++,
      name: 'Recherche',
      item: `${siteUrl}/recherche`,
    });
  } else if (data.scope.dataLayer['page.type'] === 'author') {
    itemListElement.push({
      '@type': 'ListItem',
      position: position++,
      name: 'Tous les auteurs',
      item: `${siteUrl}/authors`,
    });
  } else if (data.scope.dataLayer['page.type'] === 'tagsLetter') {
    itemListElement.push({
      '@type': 'ListItem',
      position: position++,
      name: 'Tous les tags',
      item: `${siteUrl}/tags`,
    });
  }

  const pageIndex = data.scope.dataLayer['page.index'] || 1;
  if (pageIndex > 1) {
    const url = data.scope.meta.canonical.replace(/(\?|&)page=(\d+)/, '');

    itemListElement.push({
      '@type': 'ListItem',
      position: itemListElement.length + 1,
      name: data.scope.meta.title,
      item: url,
    });
  }

  itemListElement.push({
    '@type': 'ListItem',
    position: itemListElement.length + 1,
    name: pageIndex > 1 ? `Page ${pageIndex}` : data.scope.meta.title,
  });

  const scheme: BreadcrumbList = {
    '@type': 'BreadcrumbList',
    itemListElement,
  };
  return scheme;
}
